// My Trading Journey → Share my journey: an image of the whole journey (how long, how many
// accounts, every month as a green or red square under the chapter names, the record, what
// changed and the top lesson), with the dollar amounts hidden unless the trader shows them.
// Drawn on a canvas with the same look as the results card (ShareCard.js).
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api';
import { COLORS, ImageActions, SIZES, drawBackdrop, drawBadge, drawFooter, roundRect } from './ShareCard';
import { CHAPTER_NAMES, blownLessons, chapters as storyChapters, compareStretches, journeyOverview, whatChanged } from './journey';
import { storyOf } from './JourneyStory';
import { dayKey } from './pnl';
import { useI18n } from './i18n';
import { currentLocale, translateNow as tn } from './langState';
import { trackEvent } from './analytics';

const whole = (n) => `${n < 0 ? '−' : ''}$${Math.round(Math.abs(n)).toLocaleString('en-US')}`;
const signed = (n) => `${n > 0 ? '+' : ''}${whole(n)}`;
const monthShort = (key) => new Date(`${key}-15T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', year: 'numeric' });

// Everything the card shows, from the journey's numbers.
export function journeyCard(o, parts, change, cmp, lessons, names) {
  const top = Object.values(lessons.lessons.filter((h) => h.account.lesson).reduce((acc, h) => {
    const k = h.account.lesson.trim();
    (acc[k] = acc[k] || { text: k, count: 0 }).count += 1;
    return acc;
  }, {})).sort((a, b) => b.count - a.count)[0];
  const stretch = cmp && cmp.then && cmp.now && !cmp.tooFew ? cmp : null;
  return {
    from: o.months[0].month, to: o.months[o.months.length - 1].month, months: o.months,
    chapters: parts.map((c) => ({ from: c.from, to: c.to, name: names[c.id], green: c.id === 'green' })),
    accounts: o.accounts.bought, blown: o.accounts.blown, passed: o.accounts.passed, payouts: o.payouts, paid: o.paid, streak: o.greenStreak,
    winRate: stretch ? [stretch.then.winRate, stretch.now.winRate] : null,
    lived: change && change.livedThen !== null ? [change.livedThen, change.livedNow] : null,
    perMonth: change ? [change.pnlThen, change.pnlNow] : null,
    lesson: top ? top.text : null,
  };
}

// Breaks text into lines no wider than max.
function wrap(ctx, text, max) {
  const lines = [];
  let line = '';
  for (const word of text.split(' ')) {
    const next = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(next).width > max) {
      lines.push(line);
      line = word;
    } else line = next;
  }
  if (line) lines.push(line);
  return lines;
}

function drawJourney(canvas, { card, hideAmounts, sizeId, name, badge, showLesson }) {
  const { w, h } = SIZES[sizeId];
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  const square = sizeId === 'square';
  const pad = square ? 80 : 70;
  const inner = w - pad * 2;
  drawBackdrop(ctx, w, h, pad);

  // Top right: the months it covers, the name and the badge.
  ctx.textAlign = 'right';
  ctx.fillStyle = COLORS.muted;
  ctx.font = '700 22px Inter, sans-serif';
  ctx.fillText(`${monthShort(card.from)} – ${monthShort(card.to)}`.toUpperCase(), w - pad, pad + 32);
  if (name) {
    ctx.fillStyle = COLORS.faint;
    ctx.font = '600 18px Inter, sans-serif';
    ctx.fillText(name, w - pad, pad + 58);
  }
  ctx.textAlign = 'left';
  if (badge) drawBadge(ctx, badge, w - pad, pad + (name ? 76 : 50));

  // The headline.
  let y = square ? 250 : 180;
  ctx.fillStyle = COLORS.cyan;
  ctx.font = '800 20px Inter, sans-serif';
  ctx.fillText(tn('MY TRADING JOURNEY'), pad, y);
  const headline = tn('{months} months. {accounts} accounts. Still here.', { months: card.months.length, accounts: card.accounts });
  let size = square ? 64 : 54;
  ctx.font = `800 ${size}px Inter, sans-serif`;
  let lines = wrap(ctx, headline, inner);
  while (lines.length > (square ? 2 : 1) && size > 34) {
    size -= 2;
    ctx.font = `800 ${size}px Inter, sans-serif`;
    lines = wrap(ctx, headline, inner);
  }
  ctx.fillStyle = COLORS.text;
  for (const line of lines) {
    y += size + 8;
    ctx.fillText(line, pad - 2, y);
  }

  // Every month as a square, under the chapter names, with the years below.
  const n = card.months.length;
  const gap = n > 40 ? 2 : 4;
  const cell = (inner - gap * (n - 1)) / n;
  const labelY = y + (square ? 62 : 40);
  const cellY = labelY + 12;
  const cellH = square ? 70 : 44;
  const max = Math.max(1, ...card.months.map((m) => Math.abs(m.pnl)));
  ctx.font = '700 15px Inter, sans-serif';
  for (const c of card.chapters) {
    const i = card.months.findIndex((m) => m.month === c.from);
    const j = card.months.findIndex((m) => m.month === c.to);
    const x = pad + i * (cell + gap);
    const span = (j - i + 1) * (cell + gap) - gap;
    ctx.fillStyle = c.green ? COLORS.gain : COLORS.cyan;
    ctx.fillRect(x, labelY - 15, 2, 18);
    ctx.fillStyle = c.green ? COLORS.gain : COLORS.muted;
    let label = c.name;
    while (label.length > 1 && ctx.measureText(label).width > span - 12) label = `${label.slice(0, -2)}…`;
    if (span > 40) ctx.fillText(label, x + 8, labelY);
  }
  card.months.forEach((m, i) => {
    const x = pad + i * (cell + gap);
    roundRect(ctx, x, cellY, cell, cellH, Math.min(6, cell / 3));
    if (!m.traded) {
      ctx.strokeStyle = COLORS.border;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      return;
    }
    ctx.globalAlpha = m.pnl ? 0.35 + 0.65 * Math.min(1, Math.abs(m.pnl) / max) : 0.3;
    ctx.fillStyle = m.pnl > 0 ? COLORS.gain : m.pnl < 0 ? COLORS.loss : COLORS.muted;
    ctx.fill();
    ctx.globalAlpha = 1;
  });
  ctx.fillStyle = COLORS.faint;
  ctx.font = '600 14px Inter, sans-serif';
  card.months.forEach((m, i) => {
    if (i === 0 || m.month.endsWith('-01')) ctx.fillText(m.month.slice(0, 4), pad + i * (cell + gap), cellY + cellH + 22);
  });

  // The record.
  const boxes = [
    ['ACCOUNTS BLOWN', String(card.blown)],
    ['EVALUATIONS PASSED', String(card.passed)],
    hideAmounts ? ['PAYOUTS', String(card.payouts)] : ['PAID OUT', whole(card.paid)],
    ['GREEN MONTHS IN A ROW', String(card.streak)],
  ];
  const boxY = cellY + cellH + (square ? 62 : 36);
  const boxH = square ? 104 : 80;
  const boxW = (inner - 16 * 3) / 4;
  boxes.forEach(([l, v], i) => {
    const x = pad + i * (boxW + 16);
    roundRect(ctx, x, boxY, boxW, boxH, 16);
    ctx.fillStyle = 'rgba(7, 29, 51, 0.92)';
    ctx.fill();
    ctx.strokeStyle = COLORS.border;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = COLORS.muted;
    ctx.font = '800 14px Inter, sans-serif';
    let label = tn(l);
    while (label.length > 1 && ctx.measureText(label).width > boxW - 36) label = `${label.slice(0, -2)}…`;
    ctx.fillText(label, x + 20, boxY + (square ? 32 : 28));
    ctx.fillStyle = l === 'PAID OUT' || l === 'EVALUATIONS PASSED' || l === 'GREEN MONTHS IN A ROW' ? COLORS.gain : COLORS.text;
    if (l === 'ACCOUNTS BLOWN') ctx.fillStyle = COLORS.loss;
    ctx.font = `600 ${square ? 38 : 30}px "JetBrains Mono", Menlo, monospace`;
    ctx.fillText(v, x + 18, boxY + boxH - (square ? 22 : 18));
  });

  // Then → now.
  const changes = [
    card.winRate && ['WIN RATE', `${Math.round(card.winRate[0])}%`, `${Math.round(card.winRate[1])}%`, card.winRate[1] >= card.winRate[0]],
    card.lived && ['BLOWN ACCOUNTS LASTED', tn('{n} days', { n: Math.round(card.lived[0]) }), tn('{n} days', { n: Math.round(card.lived[1]) }), card.lived[1] >= card.lived[0]],
    !hideAmounts && card.perMonth && ['RESULT PER MONTH', signed(card.perMonth[0]), signed(card.perMonth[1]), card.perMonth[1] >= card.perMonth[0]],
  ].filter(Boolean);
  let changeY = boxY + boxH + (square ? 64 : 38);
  if (changes.length) {
    ctx.fillStyle = COLORS.faint;
    ctx.font = '800 14px Inter, sans-serif';
    ctx.fillText(tn('THEN → NOW'), pad, changeY);
    const colW = inner / 3;
    changes.forEach(([l, then, now, better], i) => {
      const x = pad + i * colW;
      ctx.fillStyle = COLORS.muted;
      ctx.font = '700 15px Inter, sans-serif';
      ctx.fillText(tn(l), x, changeY + (square ? 30 : 28));
      ctx.font = `600 ${square ? 28 : 24}px "JetBrains Mono", Menlo, monospace`;
      ctx.fillStyle = COLORS.muted;
      ctx.fillText(`${then} → `, x, changeY + (square ? 68 : 60));
      const tw = ctx.measureText(`${then} → `).width;
      ctx.fillStyle = better ? COLORS.gain : COLORS.loss;
      ctx.fillText(now, x + tw, changeY + (square ? 68 : 60));
    });
    changeY += square ? 68 : 62;
  }

  // The lesson that cost the most accounts (the square card has room for it).
  if (square && showLesson && card.lesson) {
    ctx.font = 'italic 600 28px Inter, sans-serif';
    ctx.fillStyle = '#f5b941';
    let ly = changeY + 70;
    for (const line of wrap(ctx, `💡 “${card.lesson}”`, inner).slice(0, 2)) {
      ctx.fillText(line, pad, ly);
      ly += 38;
    }
  }

  drawFooter(ctx, w, h, pad);
}

export default function JourneyShare({ accounts, trades, notes, user, onClose }) {
  useEffect(() => trackEvent('Journey share card'), []);
  const { t, lang } = useI18n();
  const [expenses, setExpenses] = useState(null);
  const [hideAmounts, setHideAmounts] = useState(true);
  const [showName, setShowName] = useState(false);
  const [showLesson, setShowLesson] = useState(true);
  const [showBadge, setShowBadge] = useState(true);
  const [sizeId, setSizeId] = useState('wide');
  const badge = user && user.isCreator ? { creator: true } : user && user.foundingNumber ? { number: user.foundingNumber } : null;
  const canvas = useRef(null);
  const dialog = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => { api('/expenses').then(setExpenses).catch(() => setExpenses([])); }, []);
  const story = storyOf(user);
  const card = useMemo(() => {
    if (!expenses) return null;
    const o = journeyOverview(accounts, trades, expenses);
    if (!o) return null;
    const parts = storyChapters(o);
    const names = Object.fromEntries(parts.map((c) => {
      const own = story.chapters.find((x) => x.id === c.id);
      return [c.id, (own && own.title) || t(CHAPTER_NAMES[c.id])];
    }));
    return journeyCard(o, parts, whatChanged(o), compareStretches(trades, notes || {}, accounts, 'start'), blownLessons(o.history), names);
  }, [expenses, accounts, trades, notes, story.chapters, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dialog.current?.focus();
    const onKey = (e) => e.key === 'Escape' && closeRef.current();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!card) return undefined;
    let cancelled = false;
    Promise.all([
      document.fonts.load('800 30px Inter'), document.fonts.load('600 20px Inter'), document.fonts.load('600 36px "JetBrains Mono"'),
    ]).catch(() => {}).then(() => {
      if (!cancelled && canvas.current) drawJourney(canvas.current, { card, hideAmounts, sizeId, name: showName && user ? user.name : '', badge: showBadge ? badge : null, showLesson });
    });
    return () => { cancelled = true; };
  }, [card, hideAmounts, sizeId, showName, showLesson, showBadge, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={dialog} tabIndex={-1} className="modal modal-wide share-modal" role="dialog" aria-modal="true" aria-labelledby="js-share-title">
        <div className="modal-header">
          <div>
            <h2 id="js-share-title">{t('Share my journey')}</h2>
            <p className="js-log-sub">{t('An image of your whole journey for Discord, X or Instagram. Dollar amounts stay hidden unless you show them.')}</p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
        </div>
        <div className="share-options">
          <div className="inbox-filter" role="group" aria-label={t('Image size')}>
            {Object.entries(SIZES).map(([id, s]) => (
              <button key={id} type="button" className={`secondary small${sizeId === id ? ' active' : ''}`} onClick={() => setSizeId(id)}>{t(s.label)}</button>
            ))}
          </div>
          <label className="share-toggle">
            <input type="checkbox" checked={!hideAmounts} onChange={(e) => setHideAmounts(!e.target.checked)} />
            {t('Show dollar amounts')}
          </label>
          <label className="share-toggle">
            <input type="checkbox" checked={showName} onChange={(e) => setShowName(e.target.checked)} />
            {t('Show my name')}
          </label>
          {sizeId === 'square' && card && card.lesson && (
            <label className="share-toggle">
              <input type="checkbox" checked={showLesson} onChange={(e) => setShowLesson(e.target.checked)} />
              {t('Show my top lesson')}
            </label>
          )}
          {badge && (
            <label className="share-toggle">
              <input type="checkbox" checked={showBadge} onChange={(e) => setShowBadge(e.target.checked)} />
              {badge.creator ? t('Show my Founder badge') : t('Show my Founding Member badge')}
            </label>
          )}
        </div>
        {!expenses ? <p className="field-hint">{t('Loading…')}</p> : !card ? <p className="empty">{t('Your story starts with your first trade.')}</p> : (
          <div className="share-preview">
            <canvas ref={canvas} aria-label={t('Preview of your share image')} />
          </div>
        )}
        {card && (
          <ImageActions canvas={canvas} fileName={`market-matrix-journey-${dayKey(new Date())}.png`}
            shareText={t('My trading journey, tracked with Market Matrix: {url}', { url: lang === 'es' ? 'https://marketmatrixhq.com/es' : 'https://marketmatrixhq.com' })} />
        )}
      </div>
    </div>
  );
}
